
import { useCallback, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { usePermissionsAPI } from './usePermissionsAPI';

/**
 * Hook for managing subscription operations
 */
export const useSubscriptionManagement = (
  userId: string | undefined,
  session: Session | null,
  authReady: boolean
) => {
  const [upgradeInProgress, setUpgradeInProgress] = useState(false);
  const { initiateSubscription: apiInitiateSubscription } = usePermissionsAPI(session);

  const initiateSubscription = useCallback(async (
    priceType: 'monthly' | 'annual' = 'monthly',
    referralCode?: string
  ): Promise<void> => {
    if (!userId || !session?.access_token || !authReady) {
      throw new Error('User not authenticated');
    }

    try {
      setUpgradeInProgress(true);
      await apiInitiateSubscription(priceType, referralCode);
    } catch (error: unknown) {
      console.error('Error initiating subscription:', error);
      setUpgradeInProgress(false);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }, [userId, session, authReady, apiInitiateSubscription]);

  return {
    upgradeInProgress,
    setUpgradeInProgress,
    initiateSubscription,
  };
};
